import { useState } from 'react';
import { Box, Button, ButtonGroup, Flex, Icon, Skeleton, Text } from '@chakra-ui/react';
import { TbBox, TbCurrencyDollar, TbPercentage } from 'react-icons/tb';
import type { WidgetConfig } from './types';
import { widgetChartLayoutKey } from './types';
import InventoryTimeseriesChart from '../components/InventoryTimeseriesChart';
import type {
    InventoryMetricsSummaryResponse,
    InventoryMetricsTimeseriesResponse,
    InventorySampleType,
    ReportingComparison,
} from '../../../types/reporting';
import { getSummaryComparisonLabel } from '../shared/reportingComparisonLabels';
import {
    formatSummaryMetricChange,
    summaryChangeArrow,
    summaryChangeColor,
    type SummaryChangeTone,
} from '../shared/summaryChangeDisplay';
import {
    NEGATIVE_INVENTORY_METRICS,
    NEUTRAL_CHANGE_INVENTORY_METRICS,
    SIGNED_VALUE_INVENTORY_METRICS,
} from './inventoryCatalog';
import {
    inventoryMetricNeedsSampleType,
    resolveInventoryMetricChange,
    resolveInventoryMetricValue,
} from './inventoryMetricResolve';

export interface InventoryWidgetDataContext {
    summary?: InventoryMetricsSummaryResponse;
    summaryLoading: boolean;
    summaryError: boolean;
    timeseries?: InventoryMetricsTimeseriesResponse;
    timeseriesLoading: boolean;
    restockTimeseries?: InventoryMetricsTimeseriesResponse;
    restockTimeseriesLoading: boolean;
    comparison?: ReportingComparison;
    granularity?: 'day' | 'week' | 'biweekly' | 'month';
    preset?: string;
    startDate?: string;
    endDate?: string;
}

const formatCurrency = (v: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(v);

const formatCurrencyPrecise = (v: number) =>
    new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(v);

const formatNumber = (v: number) =>
    v.toLocaleString(undefined, { maximumFractionDigits: 1 });

function getFormatter(format: string, metric: string) {
    if (metric === 'avgOrderedValue' || metric === 'medianOrderedValue') {
        return formatCurrencyPrecise;
    }
    if (format === 'currency') return formatCurrency;
    if (format === 'percent') return (v: number) => `${v.toFixed(1)}%`;
    return formatNumber;
}

function getIcon(format: string) {
    if (format === 'currency') return TbCurrencyDollar;
    if (format === 'percent') return TbPercentage;
    return TbBox;
}

function isInStockMetric(metric: string): boolean {
    return (
        metric === 'inStockQuantity'
        || metric === 'avgInStockQuantity'
        || metric === 'medianInStockQuantity'
    );
}

function SampleTypeToggle({
    value,
    onChange,
}: {
    value: InventorySampleType;
    onChange: (next: InventorySampleType) => void;
}) {
    return (
        <ButtonGroup size="xs" isAttached variant="outline">
            <Button
                onClick={() => onChange('perUnit')}
                bg={value === 'perUnit' ? 'brand.50' : undefined}
                color={value === 'perUnit' ? 'brand.solid' : 'fg.muted'}
            >
                Per Unit
            </Button>
            <Button
                onClick={() => onChange('perItem')}
                bg={value === 'perItem' ? 'brand.50' : undefined}
                color={value === 'perItem' ? 'brand.solid' : 'fg.muted'}
            >
                Per Item
            </Button>
        </ButtonGroup>
    );
}

function signedValueColor(value: number): string {
    if (value > 0) return 'green.500';
    if (value < 0) return 'red.500';
    return 'gray.900';
}

interface Props {
    config: WidgetConfig;
    data: InventoryWidgetDataContext;
}

const InventoryWidgetRenderer = ({ config, data }: Props) => {
    const { preset } = data;
    const chartHeight = '100%';
    const [sampleType, setSampleType] = useState<InventorySampleType>('perUnit');
    const showSampleType = inventoryMetricNeedsSampleType(config.metric);

    if (config.widgetType === 'keyMetric') {
        const isLoading = data.summaryLoading;
        const summaryData = data.summary;
        const value = resolveInventoryMetricValue(summaryData, config.metric, sampleType);
        const fmt = getFormatter(config.format, config.metric);
        const IconComp = getIcon(config.format);

        const comparisonEnabled = config.comparison !== false;
        let changeDisplay = formatSummaryMetricChange(
            resolveInventoryMetricChange(summaryData, config.metric, sampleType),
            {
                suffix: config.format === 'percent' ? 'pp' : '%',
                isNegativeMetric: NEGATIVE_INVENTORY_METRICS.has(config.metric),
                comparisonEnabled,
            },
        );

        if (changeDisplay && NEUTRAL_CHANGE_INVENTORY_METRICS.has(config.metric)) {
            changeDisplay = { ...changeDisplay, tone: 'neutral' as SummaryChangeTone };
        }

        const changePeriod = getSummaryComparisonLabel(preset);
        const valueColor =
            value !== undefined && SIGNED_VALUE_INVENTORY_METRICS.has(config.metric)
                ? signedValueColor(value)
                : 'gray.900';

        return (
            <Flex direction="column" gap={2} w="100%">
                {showSampleType && (
                    <Flex justify="flex-end">
                        <SampleTypeToggle value={sampleType} onChange={setSampleType} />
                    </Flex>
                )}
                <Flex align="center">
                    <Flex
                        alignItems="center"
                        justifyContent="center"
                        w={9}
                        h={9}
                        borderRadius="full"
                        bg="gray.100"
                        mr={3}
                        flexShrink={0}
                    >
                        <Icon as={IconComp as React.ElementType} w={5} h={5} color="brand.solid" />
                    </Flex>
                    <Box flex="1" minW={0}>
                        <Skeleton
                            isLoaded={!isLoading}
                            h={isLoading ? '28px' : 'auto'}
                            w={isLoading ? '100px' : 'auto'}
                            borderRadius="md"
                        >
                            <Flex alignItems="baseline" gap={2}>
                                <Text fontSize="xl" fontWeight="bold" color={valueColor} lineHeight="tight">
                                    {data.summaryError
                                        ? '—'
                                        : value !== undefined
                                            ? fmt(value)
                                            : '—'}
                                </Text>
                                {!isLoading && changeDisplay && (
                                    <Text
                                        fontSize="xs"
                                        fontWeight="medium"
                                        color={summaryChangeColor(changeDisplay.tone)}
                                    >
                                        {summaryChangeArrow(changeDisplay.tone)}
                                        {changeDisplay.text}
                                        {changePeriod && (
                                            <Text as="span" color="gray.400" fontWeight="normal" ml={1}>
                                                {changePeriod}
                                            </Text>
                                        )}
                                    </Text>
                                )}
                            </Flex>
                        </Skeleton>
                    </Box>
                </Flex>
            </Flex>
        );
    }

    if (config.widgetType === 'timeSeries') {
        const restockOnly =
            isInStockMetric(config.metric) && Boolean(config.restockItemsOnly);
        const series = restockOnly ? data.restockTimeseries : data.timeseries;
        const seriesLoading = restockOnly
            ? data.restockTimeseriesLoading
            : data.timeseriesLoading;

        return (
            <Box h="100%" w="100%" minH={0} minW={0}>
                <InventoryTimeseriesChart
                    key={widgetChartLayoutKey(config)}
                    data={series}
                    isLoading={seriesLoading}
                    height={chartHeight}
                    metric={config.metric}
                    chartStyle={config.chartStyle ?? 'bars'}
                    comparison={data.comparison}
                    showComparisonBars={Boolean(config.showComparisonBars)}
                    showTargetReorderRefs={restockOnly}
                    granularity={data.granularity}
                    preset={data.preset}
                    sampleType={sampleType}
                    onSampleTypeChange={showSampleType ? setSampleType : undefined}
                />
            </Box>
        );
    }

    return (
        <Text color="gray.400" fontSize="sm">
            Unsupported widget type
        </Text>
    );
};

export default InventoryWidgetRenderer;
